import React, { useState } from 'react';
import { Typography, Space, Image, Modal } from 'antd';
import {
  DownloadOutlined,
  LeftOutlined,
  RightOutlined,
  RotateLeftOutlined,
  RotateRightOutlined,
  SwapOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
  UndoOutlined,
  PlayCircleFilled,
  CloseOutlined,
} from '@ant-design/icons';
import { Box } from '@mui/material';

// --- Types ---
interface MediaItem {
  url: string;
  type: 'image' | 'video';
}

interface VehicleMediaProps {
  vehicle: {
    vehicleImage?: { vehicleImageUrl: string }[];
    vehicleVideo?: { vehicleVideoUrl: string }[];
  };
}

// --- Styles ---
const iconStyle: React.CSSProperties = {
  color: '#fff',
  fontSize: 20,
  cursor: 'pointer',
  padding: '4px',
  transition: '0.2s',
};

// --- Helper: Custom Image Toolbar ---
const CustomToolbar = ({
  onActive,
  onFlipY,
  onFlipX,
  onRotateLeft,
  onRotateRight,
  onZoomOut,
  onZoomIn,
  onReset,
  currentUrl,
  scale,
}: any) => {
  const handleDownload = () => {
    const suffix = currentUrl.slice(currentUrl.lastIndexOf('.'));
    const filename = `vehicle-image-${Date.now()}${suffix}`;
    fetch(currentUrl)
      .then((res) => res.blob())
      .then((blob) => {
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        URL.revokeObjectURL(blobUrl);
        link.remove();
      });
  };

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 50, // Moved to bottom for better mobile UX
        left: '50%',
        transform: 'translateX(-50%)',
        backgroundColor: 'rgba(0,0,0,0.6)',
        padding: '10px 20px',
        borderRadius: 30,
        zIndex: 1601,
        display: 'flex',
        gap: 24,
        alignItems: 'center',
        backdropFilter: 'blur(10px)',
      }}
    >
      <DownloadOutlined style={iconStyle} onClick={handleDownload} />
      <SwapOutlined rotate={90} style={iconStyle} onClick={onFlipY} />
      <SwapOutlined style={iconStyle} onClick={onFlipX} />
      <RotateLeftOutlined style={iconStyle} onClick={onRotateLeft} />
      <RotateRightOutlined style={iconStyle} onClick={onRotateRight} />
      <ZoomOutOutlined style={iconStyle} onClick={onZoomOut} disabled={scale === 1} />
      <ZoomInOutlined style={iconStyle} onClick={onZoomIn} disabled={scale === 50} />
      <UndoOutlined style={iconStyle} onClick={onReset} />
    </div>
  );
};

// --- Main Component ---
const VehicleMedia: React.FC<VehicleMediaProps> = ({ vehicle }) => {
  const [previewIndex, setPreviewIndex] = useState(0);
  const [imageModalVisible, setImageModalVisible] = useState(false);
  
  // Video Modal State
  const [videoState, setVideoState] = useState<{
    isOpen: boolean;
    currentIndex: number;
  }>({
    isOpen: false,
    currentIndex: 0,
  });

  const imageList = vehicle.vehicleImage ?? [];
  const videoList = vehicle.vehicleVideo ?? [];

  // Handlers for Video Navigation
  const handleVideoNext = () => {
    setVideoState((prev) => ({
      ...prev,
      currentIndex: (prev.currentIndex + 1) % videoList.length,
    }));
  };

  const handleVideoPrev = () => {
    setVideoState((prev) => ({
      ...prev,
      currentIndex: (prev.currentIndex - 1 + videoList.length) % videoList.length,
    }));
  };

  const currentVideoUrl = videoList[videoState.currentIndex]?.vehicleVideoUrl;

  return (
    <div style={{ marginTop: 24 }}>
      
      {/* --- 1. Vehicle Images Section --- */}
      {imageList.length > 0 && (
        <Box mb={4}>
          <Typography style={{ marginBottom: 12, fontWeight: 700, fontSize: 18 }}>
            Vehicle Images ({imageList.length})
          </Typography>

          <Space wrap size={[12, 12]}>
            {imageList.map((img, idx) => (
              <Box
                key={idx}
                sx={{
                  width: 120,
                  height: 90,
                  borderRadius: 2,
                  overflow: 'hidden',
                  border: '1px solid #e0e0e0',
                  cursor: 'pointer',
                  transition: 'transform 0.2s',
                  '&:hover': { transform: 'scale(1.05)', boxShadow: 2 },
                }}
              >
                <Image
                  src={img.vehicleImageUrl}
                  width="100%"
                  height="100%"
                  style={{ objectFit: 'cover' }}
                  preview={false}
                  placeholder={
                    <div style={{ backgroundColor: '#f0f0f0', width: '100%', height: '100%' }} />
                  }
                  onClick={() => {
                    setPreviewIndex(idx);
                    setImageModalVisible(true);
                  }}
                />
              </Box>
            ))}
          </Space>

          {/* Ant Design Image Preview Group */}
          <div style={{ display: 'none' }}>
            <Image.PreviewGroup
              preview={{
                visible: imageModalVisible,
                current: previewIndex,
                onVisibleChange: (vis) => setImageModalVisible(vis),
                onChange: (index) => setPreviewIndex(index),
                toolbarRender: (originalNode, info) => (
                  <CustomToolbar
                    {...info.actions}
                    scale={info.transform.scale}
                    currentUrl={imageList[previewIndex]?.vehicleImageUrl}
                  />
                ),
              }}
            >
              {imageList.map((img, idx) => (
                <Image key={idx} src={img.vehicleImageUrl} />
              ))}
            </Image.PreviewGroup>
          </div>
        </Box>
      )}

      {/* --- 2. Vehicle Videos Section --- */}
      {videoList.length > 0 && (
        <Box>
          <Typography style={{ marginBottom: 12, fontWeight: 700, fontSize: 18 }}>
            Vehicle Videos ({videoList.length})
          </Typography>

          <Space wrap size={[12, 12]}>
            {videoList.map((video, idx) => (
              <Box
                key={idx}
                onClick={() => setVideoState({ isOpen: true, currentIndex: idx })}
                sx={{
                  width: 160,
                  height: 100,
                  backgroundColor: '#000',
                  borderRadius: 2,
                  overflow: 'hidden',
                  position: 'relative',
                  cursor: 'pointer',
                  border: '1px solid #e0e0e0',
                  '&:hover': { opacity: 0.9 },
                }}
              >
                <video
                  src={video.vehicleVideoUrl}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.7 }}
                  muted
                />
                {/* Overlay Play Icon */}
                <div
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 2,
                  }}
                >
                  <PlayCircleFilled style={{ fontSize: 32, color: '#fff', opacity: 0.9 }} />
                </div>
              </Box>
            ))}
          </Space>

          {/* Custom Video Modal */}
          <Modal
            open={videoState.isOpen}
            onCancel={() => setVideoState((prev) => ({ ...prev, isOpen: false }))}
            footer={null}
            centered
            destroyOnClose
            width={800}
            closable={false} // Custom close button used
            styles={{
              body: { padding: 0, backgroundColor: '#000', borderRadius: 8, overflow: 'hidden' },
              mask: { backgroundColor: 'rgba(0,0,0,0.9)' },
            }}
          >
            <div style={{ position: 'relative', width: '100%', aspectRatio: '16/9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              
              {/* Close Button */}
              <CloseOutlined
                onClick={() => setVideoState((prev) => ({ ...prev, isOpen: false }))}
                style={{
                  position: 'absolute',
                  top: 16,
                  right: 16,
                  fontSize: 24,
                  color: '#fff',
                  cursor: 'pointer',
                  zIndex: 10,
                  background: 'rgba(0,0,0,0.5)',
                  padding: 8,
                  borderRadius: '50%'
                }}
              />

              {/* Navigation Arrows (Only if multiple videos) */}
              {videoList.length > 1 && (
                <>
                  <LeftOutlined
                    onClick={(e) => { e.stopPropagation(); handleVideoPrev(); }}
                    style={{
                      position: 'absolute',
                      left: 20,
                      fontSize: 40,
                      color: '#fff',
                      cursor: 'pointer',
                      zIndex: 10,
                      background: 'rgba(0,0,0,0.3)',
                      borderRadius: '50%',
                      padding: 8
                    }}
                  />
                  <RightOutlined
                    onClick={(e) => { e.stopPropagation(); handleVideoNext(); }}
                    style={{
                      position: 'absolute',
                      right: 20,
                      fontSize: 40,
                      color: '#fff',
                      cursor: 'pointer',
                      zIndex: 10,
                      background: 'rgba(0,0,0,0.3)',
                      borderRadius: '50%',
                      padding: 8
                    }}
                  />
                </>
              )}

              {/* Video Player */}
              {currentVideoUrl && (
                <video
                  key={currentVideoUrl} // Forces re-render on video change
                  src={currentVideoUrl}
                  controls
                  autoPlay
                  style={{ width: '100%', height: '100%', maxHeight: '80vh' }}
                />
              )}
            </div>
          </Modal>
        </Box>
      )}
    </div>
  );
};

export default VehicleMedia;